import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo_medium.png";
import { supabase } from "@/integrations/supabase/client";
import { Step2EmailValidation } from "./steps/Step2EmailValidation";
import { Step1PatientRegistration, type PatientRegistrationForm } from "./steps/Step1PatientRegistration";
import { getHomePathForRoles } from "@/lib/role-routing";
import { setOnboardingOrigin } from "@/lib/onboarding-origin";
import type { HealthArea } from "@/hooks/useHealthAreas";
import { selectPatientHealthArea } from "@/hooks/useHealthAreas";
import { PATIENT_ONBOARDING_ACCOUNT_TYPE } from "@/lib/onboarding/onboarding.schemas";
import type { User } from "@supabase/supabase-js";

/**
 * Garante profiles.health_area_id para paciente: RPC SECURITY DEFINER (RLS não bloqueia)
 * quando ainda está NULL; fallback update direto se RPC não existir (projeto sem migration).
 */
async function ensurePatientProfileHealthArea(
  user: User,
  patientAreaFallback: HealthArea | undefined,
): Promise<void> {
  const raw = user.user_metadata?.health_area_id;
  const fromMeta =
    typeof raw === "string" && raw.trim().length > 0 ? raw.trim() : null;
  const targetId = fromMeta ?? patientAreaFallback?.id;
  if (!targetId) return;

  const { data: profile, error: readError } = await supabase
    .from("profiles")
    .select("health_area_id")
    .eq("id", user.id)
    .maybeSingle();

  if (readError) {
    console.error("[OnboardingPaciente] profile read for health_area_id:", readError);
    return;
  }

  if (profile?.health_area_id) return;

  const { data: filled, error: rpcError } = await (supabase.rpc as any)(
    "fill_profile_health_area_if_missing",
    { p_health_area_id: targetId },
  );

  if (!rpcError && filled === true) return;

  if (rpcError) {
    console.warn(
      "[OnboardingPaciente] fill_profile_health_area_if_missing (tentando update direto):",
      rpcError.message,
    );
  }

  const { error: updateError } = await supabase
    .from("profiles")
    .update({ health_area_id: targetId })
    .eq("id", user.id)
    .is("health_area_id", null);

  if (updateError) {
    console.error("[OnboardingPaciente] profile health_area_id fallback update:", updateError);
  }
}

const stepTransition = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
};

function parseFocusParam(
  raw: string | null,
): "name" | "email" | "password" | undefined {
  if (!raw) return undefined;
  const v = raw.trim().toLowerCase();
  if (v === "name" || v === "email" || v === "password") return v;
  return undefined;
}

export default function OnboardingPaciente() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<1 | 2>(1);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string>("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [formData, setFormData] = useState<PatientRegistrationForm>({
    name: "",
    email: "",
    password: "",
  });
  const [allHealthAreas, setAllHealthAreas] = useState<HealthArea[]>([]);

  const patientHealthArea = useMemo(
    () => selectPatientHealthArea(allHealthAreas),
    [allHealthAreas],
  );

  useEffect(() => {
    setOnboardingOrigin("patient");
  }, []);

  useEffect(() => {
    const urlName = searchParams.get("name");
    const urlFullName = searchParams.get("full_name");
    const urlEmail = searchParams.get("email");

    const nameFromUrl =
      (urlName?.trim() || urlFullName?.trim() || "") || undefined;
    const emailFromUrl = urlEmail?.trim() || undefined;

    if (!nameFromUrl && !emailFromUrl) return;

    setFormData((prev) => ({
      ...prev,
      ...(nameFromUrl !== undefined ? { name: nameFromUrl } : {}),
      ...(emailFromUrl !== undefined ? { email: emailFromUrl } : {}),
    }));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from("health_areas")
        .select("id, name")
        .eq("is_active", true)
        .order("display_order");
      if (error) {
        console.error("Erro ao carregar áreas de saúde:", error);
        return;
      }
      if (!cancelled) setAllHealthAreas(data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sessionUserId || !patientHealthArea?.id) return;
    void (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      await ensurePatientProfileHealthArea(user, patientHealthArea);
    })();
  }, [sessionUserId, patientHealthArea]);

  useEffect(() => {
    const loadSessionAndProfile = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      const user = session?.user;

      if (!user) {
        setStep(localStorage.getItem("pending_email_confirmation") ? 2 : 1);
        setSessionUserId(null);
        setSessionEmail("");
        return;
      }

      setSessionUserId(user.id);
      setSessionEmail(user.email ?? "");

      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_completed,email_confirmed")
        .eq("id", user.id)
        .single();

      if (profile?.onboarding_completed && profile?.email_confirmed) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);
        nav(getHomePathForRoles((roles ?? []).map((r) => r.role)), { replace: true });
        return;
      }

      if (profile?.onboarding_completed && !profile?.email_confirmed) {
        setStep(2);
        return;
      }

      if (profile?.email_confirmed) {
        await supabase
          .from("profiles")
          .update({ onboarding_completed: true })
          .eq("id", user.id);

        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);
        nav(getHomePathForRoles((roles ?? []).map((r) => r.role)), { replace: true });
        return;
      }

      setStep(2);
    };

    void loadSessionAndProfile();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, currentSession) => {
      const user = currentSession?.user;
      if (!user) return;
      setSessionUserId(user.id);
      setSessionEmail(user.email ?? "");
      setStep(2);
    });

    return () => subscription.unsubscribe();
  }, [nav]);

  const handleRegister = async (data: PatientRegistrationForm) => {
    if (!patientHealthArea?.id) {
      toast.error(
        "Não foi possível carregar a área de saúde \"Paciente\". Tente novamente em instantes.",
      );
      return;
    }

    setIsRegistering(true);
    try {
      const emailRedirectTo = `${window.location.origin}/auth/verify-email?origin=patient`;

      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo,
          data: {
            full_name: data.name,
            account_type: PATIENT_ONBOARDING_ACCOUNT_TYPE,
            // Metadados do GoTrue: string explícita ajuda o trigger a ler o UUID
            health_area_id: String(patientHealthArea.id),
          },
        },
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      if (!authData.user) {
        toast.error("Erro ao criar usuário");
        return;
      }

      await ensurePatientProfileHealthArea(authData.user, patientHealthArea);

      const accessToken =
        authData.session?.access_token ??
        (await supabase.auth.getSession()).data.session?.access_token;

      if (accessToken) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: anonKey,
          },
          body: JSON.stringify({
            user_id: authData.user.id,
            email: data.email,
            name: data.name,
            origin: "patient",
          }),
        });
      } else {
        toast.error(
          "Conta criada, mas não há sessão ativa para enviar o e-mail de confirmação. Faça login e use reenviar.",
        );
      }

      localStorage.setItem("pending_email_confirmation", data.email);
      setOnboardingOrigin("patient");
      toast.success("Conta criada! Verifique seu e-mail para confirmar.");
      setStep(2);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao criar conta. Tente novamente.");
    } finally {
      setIsRegistering(false);
    }
  };

  const handleResendEmail = async () => {
    const email = sessionEmail || localStorage.getItem("pending_email_confirmation") || "";
    if (!email) {
      toast.error("E-mail não encontrado. Faça o cadastro novamente.");
      setStep(1);
      return;
    }

    setIsResending(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.email || !session.access_token) {
        toast.error("Sessão expirada. Faça login para reenviar o e-mail.");
        nav("/auth");
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({
          user_id: session.user.id,
          email: session.user.email,
          origin: "patient",
        }),
      });

      const result = await res.json();
      if (!res.ok) {
        toast.error(result.error ?? "Erro ao reenviar e-mail");
        return;
      }

      toast.success("E-mail reenviado com sucesso.");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao reenviar e-mail. Tente novamente.");
    } finally {
      setIsResending(false);
    }
  };

  const handleAlreadyConfirmed = async () => {
    if (!sessionUserId) {
      toast.error("Faça login para confirmar sua conta.");
      nav("/auth");
      return;
    }

    const { data } = await supabase
      .from("profiles")
      .select("email_confirmed")
      .eq("id", sessionUserId)
      .single();

    if (!data?.email_confirmed) {
      toast.error("E-mail ainda não confirmado. Verifique sua caixa de entrada.");
      return;
    }

    const {
      data: { user: authUser },
    } = await supabase.auth.getUser();
    if (authUser) {
      await ensurePatientProfileHealthArea(authUser, patientHealthArea);
    }

    await supabase
      .from("profiles")
      .update({ onboarding_completed: true })
      .eq("id", sessionUserId);

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", sessionUserId);
    nav(getHomePathForRoles((roles ?? []).map((r) => r.role)), { replace: true });
  };

  const handleChangeEmail = async () => {
    setIsChangingEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-own-account");
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      localStorage.removeItem("pending_email_confirmation");
      setFormData({ name: "", email: "", password: "" });
      setSessionUserId(null);
      setSessionEmail("");
      setStep(1);
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Erro ao excluir conta:", error);
      toast.error("Não foi possível trocar o e-mail agora. Tente novamente.");
    } finally {
      setIsChangingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-accent/10 to-background">
      <div className="max-w-5xl mx-auto py-12 px-4 space-y-8">
        <motion.header
          className="text-center mb-8 space-y-4"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <img src={logo} alt="Elinia" className="h-auto w-auto mx-auto mb-6" />
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-full px-4 py-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Onboarding do paciente</span>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Complete seu cadastro e confirme seu e-mail para acessar sua área de saúde.
          </p>
        </motion.header>

        <div className="max-w-4xl mx-auto mb-12 px-4">
          <div className="grid grid-cols-2 relative">
            <div
              className="absolute top-5 left-[calc(25%+24px)] right-[calc(25%+24px)] h-0.5 bg-muted rounded-full overflow-hidden"
              aria-hidden
            >
              <motion.div
                className="h-full bg-gradient-to-r from-primary to-secondary rounded-full"
                initial={false}
                animate={{ width: step === 2 ? "100%" : "0%" }}
                transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              />
            </div>

            {[
              { num: 1, label: "Cadastro" },
              { num: 2, label: "Validação\ne-mail" },
            ].map(({ num, label }) => (
              <div key={num} className="flex flex-col items-center gap-2 relative z-10">
                <div
                  className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold transition-all duration-300 bg-background ${
                    step >= num
                      ? "bg-gradient-to-br from-primary to-primary-glow text-white shadow-lg shadow-primary/25"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {step > num ? <CheckCircle2 className="h-5 w-5" /> : num}
                </div>
                <span className="text-[10px] font-medium text-center leading-tight whitespace-pre-line">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1-patient" {...stepTransition}>
              <Step1PatientRegistration
                formData={formData}
                onFormChange={(partial) => setFormData((prev) => ({ ...prev, ...partial }))}
                onRegister={handleRegister}
                isRegistering={isRegistering}
                initialFocus={parseFocusParam(searchParams.get("focus"))}
              />
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2-patient" {...stepTransition}>
              <Step2EmailValidation
                email={sessionEmail || localStorage.getItem("pending_email_confirmation") || ""}
                onResendEmail={handleResendEmail}
                onAlreadyConfirmed={handleAlreadyConfirmed}
                onChangeEmail={handleChangeEmail}
                isResending={isResending}
                isChangingEmail={isChangingEmail}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
